import {
  useRouteError,
  isRouteErrorResponse,
  useNavigate,
} from "react-router-dom";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Route-level error component for React Router error handling
 * Handles both route errors and dynamic import failures
 */
export default function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();

  // Determine error type and message
  let errorMessage = "An unexpected error occurred.";
  let errorStatus: number | null = null;
  let isChunkError = false;

  if (isRouteErrorResponse(error)) {
    errorStatus = error.status;
    errorMessage = error.statusText || error.data?.message || errorMessage;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    isChunkError =
      error.name === "ChunkLoadError" ||
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.message.toLowerCase().includes("chunk");
  }

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-surface border border-surface-border rounded-lg p-6 space-y-4">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="rounded-full bg-error/10 p-4">
            <AlertTriangle className="w-12 h-12 text-error" />
          </div>
        </div>

        {/* Title */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-white">
            {errorStatus
              ? `Error ${errorStatus}`
              : isChunkError
              ? "Update Required"
              : "Oops!"}
          </h1>
          <p className="text-muted text-lg">
            {isChunkError
              ? "The application has been updated and needs to be reloaded."
              : errorMessage}
          </p>
        </div>

        {/* Error Details */}
        {error instanceof Error && (
          <div className="bg-surface-dark rounded-lg p-4 border border-surface-border">
            <p className="text-xs font-semibold text-white mb-2">
              Technical Details:
            </p>
            <p className="text-xs text-error font-mono break-all">
              {error.name}: {error.message}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {isChunkError ? (
            <Button onClick={handleReload} className="w-full" size="lg">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload Application
            </Button>
          ) : (
            <>
              <Button onClick={handleGoHome} className="w-full" size="lg">
                <Home className="w-4 h-4 mr-2" />
                Go To Projects Page
              </Button>
              <Button
                onClick={handleReload}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
            </>
          )}
        </div>

        {/* Help Text */}
        <p className="text-xs text-muted text-center">
          If the problem persists, please contact support.
        </p>
      </div>
    </div>
  );
}
